import React, { useEffect, useState } from 'react';
import { Card } from '../../components/base/Card';
import { Button } from '../../components/base/Button';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

export const QualityPage: React.FC = () => {
  const [pageData, setPageData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPageData();
  }, []);

  const fetchPageData = async () => {
    try {
      const { data, error } = await supabase
        .from('pages')
        .select('*')
        .eq('slug', 'kalite')
        .eq('is_published', true)
        .single();

      if (error) throw error;
      if (data) {
        setPageData(data);
      }
    } catch (error) {
      console.error('Sayfa yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#C1121F]"></div>
      </div>
    );
  }

  const certificates = [
    {
      title: 'ISO 9001:2015',
      description: 'Kalite Yönetim Sistemi Sertifikası',
      year: '2018',
      authority: 'TSE - Türk Standardları Enstitüsü',
      image: 'https://public.readdy.ai/ai/img_res/dc33eecc8adfc28cfcf43646352374a2.jpg'
    },
    {
      title: 'ISO 3834-2',
      description: 'Kaynak Kalite Yönetim Sistemi',
      year: '2020',
      authority: 'Türk Loydu Vakfı',
      image: 'https://readdy.ai/api/search-image?query=ISO%203834%20welding%20quality%20certificate%2C%20professional%20certification%20document%20with%20Turkish%20authority%20seal&width=300&height=400&seq=welding-cert&orientation=portrait'
    },
    {
      title: 'EN 1090-1',
      description: 'Çelik Yapı İmalat Sertifikası',
      year: '2019',
      authority: 'TÜRKAK Akreditasyonlu Kuruluş',
      image: 'https://readdy.ai/api/search-image?query=EN%201090%20steel%20construction%20manufacturing%20certificate%2C%20official%20document%20with%20red%20accents%20and%20Turkish%20certification&width=300&height=400&seq=steel-cert&orientation=portrait'
    }
  ];

  const qualityStandards = [
    {
      icon: 'ri-focus-3-line',
      title: 'Hassasiyet Kontrolü',
      description: 'Mikron seviyesinde ölçüm ve kontrol',
      details: ['CMM koordinat ölçüm makinesi', 'Dijital kumpas ve mikrometreler', 'Yüzey pürüzlülük ölçümü', 'Geometrik tolerans kontrolü']
    },
    {
      icon: 'ri-eye-line',
      title: 'Görsel Muayene',
      description: 'Detaylı görsel kalite kontrolü',
      details: ['Yüzey kalitesi değerlendirmesi', 'Kaynak dikişi muayenesi', 'Boyutsal kontrol', 'Estetik değerlendirme']
    },
    {
      icon: 'ri-test-tube-line',
      title: 'Malzeme Testleri',
      description: 'Malzeme özelliklerinin doğrulanması',
      details: ['Sertlik ölçümü', 'Çekme dayanımı testi', 'Kimyasal analiz', 'Metalografi incelemesi']
    },
    {
      icon: 'ri-file-text-line',
      title: 'Dokümantasyon',
      description: 'Detaylı kalite raporları',
      details: ['Ölçüm raporları', 'Test sertifikaları', 'Malzeme sertifikaları', 'İşlem prosedürleri']
    }
  ];

  const measurementTools = [
    {
      name: 'CMM Koordinat Ölçüm Makinesi',
      accuracy: '±0.002mm',
      range: '600x800x500mm',
      description: '3D koordinat ölçümü ve geometrik tolerans kontrolü',
      image: 'https://readdy.ai/api/search-image?query=CMM%20coordinate%20measuring%20machine%20in%20Turkish%20quality%20control%20lab%2C%20precision%20measurement%20equipment%2C%20red%20and%20white%20industrial%20lighting&width=400&height=300&seq=cmm-machine&orientation=landscape'
    },
    {
      name: 'Dijital Kumpas Seti',
      accuracy: '±0.01mm',
      range: '0-300mm',
      description: 'Hassas boyutsal ölçümler için dijital kumpaslar',
      image: 'https://readdy.ai/api/search-image?query=Digital%20calipers%20and%20precision%20measuring%20tools%20set%2C%20quality%20control%20instruments%2C%20red%20and%20white%20workshop%20background&width=400&height=300&seq=digital-calipers&orientation=landscape'
    },
    {
      name: 'Yüzey Pürüzlülük Ölçer',
      accuracy: 'Ra 0.01μm',
      range: 'Ra 0.05-40μm',
      description: 'Yüzey kalitesi ve pürüzlülük ölçümü',
      image: 'https://readdy.ai/api/search-image?query=Surface%20roughness%20measurement%20device%20testing%20metal%20part%20quality%2C%20precision%20measurement%20in%20Turkish%20workshop&width=400&height=300&seq=roughness-meter&orientation=landscape'
    },
    {
      name: 'Sertlik Ölçüm Cihazı',
      accuracy: '±1 HRC',
      range: '20-70 HRC',
      description: 'Malzeme sertlik değerlerinin ölçümü',
      image: 'https://readdy.ai/api/search-image?query=Hardness%20testing%20equipment%20measuring%20steel%20part%20hardness%2C%20quality%20control%20device%2C%20red%20and%20white%20industrial%20setting&width=400&height=300&seq=hardness-tester&orientation=landscape'
    }
  ];

  const qualityProcess = [
    {
      step: '01',
      title: 'Gelen Malzeme Kontrolü',
      description: 'Ham madde kalite kontrolü ve sertifika doğrulaması',
      icon: 'ri-inbox-line'
    },
    {
      step: '02',
      title: 'Süreç İçi Kontrol',
      description: 'Üretim aşamalarında ara kontroller',
      icon: 'ri-settings-3-line'
    },
    {
      step: '03',
      title: 'Final Muayene',
      description: 'Tamamlanan ürünlerin detaylı kontrolü',
      icon: 'ri-search-eye-line'
    },
    {
      step: '04',
      title: 'Kalite Raporu',
      description: 'Ölçüm sonuçları ve kalite belgelerinin hazırlanması',
      icon: 'ri-file-check-line'
    }
  ];

  const qualityMetrics = [
    { metric: 'Hatasız Teslimat', value: '%99.2', icon: 'ri-checkbox-circle-line' },
    { metric: 'Zamanında Teslimat', value: '%98.5', icon: 'ri-time-line' },
    { metric: 'Müşteri Memnuniyeti', value: '%97.8', icon: 'ri-heart-line' },
    { metric: 'Kalite Skoru', value: '9.8/10', icon: 'ri-star-line' }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section 
        className="relative h-96 bg-cover bg-center bg-no-repeat flex items-center"
        style={{
          backgroundImage: `linear-gradient(rgba(22, 22, 22, 0.7), rgba(22, 22, 22, 0.7)), url('https://readdy.ai/api/search-image?query=Quality%20control%20laboratory%20with%20precision%20measurement%20equipment%2C%20CMM%20machine%20and%20testing%20instruments%2C%20red%20and%20white%20Turkish%20facility&width=1920&height=600&seq=quality-hero&orientation=landscape')`
        }}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-3xl text-white">
            <h1 className="text-5xl font-bold mb-6" style={{ fontFamily: '"Poppins", sans-serif' }}>
              Kalite & Sertifikalar
            </h1>
            <p className="text-xl leading-relaxed">
              ISO belgeleri, ölçüm ekipmanları ve titiz kalite kontrol süreçleri. 
              Her üretim aşamasında mükemmellik standardını yakalıyoruz.
            </p>
          </div>
        </div>
      </section>

      {/* Quality Metrics */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {qualityMetrics.map((metric, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-[#C1121F] rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className={`${metric.icon} text-white text-2xl`}></i>
                </div>
                <div className="text-3xl font-bold text-[#161616] mb-2">{metric.value}</div>
                <div className="text-gray-600">{metric.metric}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certificates */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#161616] mb-6" style={{ fontFamily: '"Poppins", sans-serif' }}>
              Sertifikalarımız
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Uluslararası standartlara uygun kalite yönetim sistemleri ve sertifikalar.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {certificates.map((cert, index) => (
              <Card key={index} hover className="text-center">
                <img 
                  src={cert.image}
                  alt={cert.title}
                  className="w-48 h-64 mx-auto mb-6 object-cover object-top rounded-lg shadow-md"
                />
                <h3 className="text-xl font-bold text-[#161616] mb-2">
                  {cert.title}
                </h3>
                <p className="text-gray-600 mb-3">
                  {cert.description}
                </p>
                <div className="text-sm text-gray-500 mb-2">
                  Alındığı Yıl: {cert.year}
                </div>
                <div className="text-sm text-[#C1121F] font-medium">
                  {cert.authority}
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Quality Standards */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#161616] mb-6" style={{ fontFamily: '"Poppins", sans-serif' }}>
              Kalite Kontrol Süreçlerimiz
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Her üretim aşamasında titiz kalite kontrol uygulamaları gerçekleştiriyoruz.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {qualityStandards.map((standard, index) => (
              <Card key={index} hover>
                <div className="w-16 h-16 bg-[#C1121F] rounded-full flex items-center justify-center mb-6">
                  <i className={`${standard.icon} text-white text-2xl`}></i>
                </div>
                <h3 className="text-xl font-semibold text-[#161616] mb-4">
                  {standard.title}
                </h3>
                <p className="text-gray-600 mb-4">
                  {standard.description}
                </p>
                <ul className="space-y-2">
                  {standard.details.map((detail, idx) => (
                    <li key={idx} className="flex items-center text-sm text-gray-600">
                      <i className="ri-check-line text-[#C1121F] mr-2"></i>
                      {detail}
                    </li>
                  ))}
                </ul>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Measurement Tools */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#161616] mb-6" style={{ fontFamily: '"Poppins", sans-serif' }}>
              Ölçüm Ekipmanlarımız
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Modern ölçüm cihazları ile mikron hassasiyetinde kalite kontrolü yapıyoruz.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {measurementTools.map((tool, index) => (
              <Card key={index} hover className="overflow-hidden">
                <div className="flex flex-col lg:flex-row">
                  <div className="lg:w-1/2">
                    <img 
                      src={tool.image}
                      alt={tool.name}
                      className="w-full h-48 lg:h-full object-cover object-top"
                    />
                  </div>
                  <div className="lg:w-1/2 p-6">
                    <h3 className="text-xl font-bold text-[#161616] mb-3">
                      {tool.name}
                    </h3>
                    <p className="text-gray-600 mb-4">
                      {tool.description}
                    </p>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Hassasiyet:</span>
                        <span className="font-medium text-[#C1121F]">{tool.accuracy}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Ölçüm Aralığı:</span>
                        <span className="font-medium text-gray-700">{tool.range}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Quality Process */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#161616] mb-6" style={{ fontFamily: '"Poppins", sans-serif' }}>
              Kalite Kontrol Süreci
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Ham maddeden teslimata kadar her aşamada kalite güvencesi.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {qualityProcess.map((process, index) => (
              <div key={index} className="text-center">
                <div className="w-20 h-20 bg-[#C1121F] rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-2xl font-bold text-white">{process.step}</span>
                </div>
                <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <i className={`${process.icon} text-[#C1121F] text-xl`}></i>
                </div>
                <h3 className="text-xl font-semibold text-[#161616] mb-4">
                  {process.title}
                </h3>
                <p className="text-gray-600">
                  {process.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quality Reports */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#161616] mb-6" style={{ fontFamily: '"Poppins", sans-serif' }}>
              Kalite Raporları
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Her proje için detaylı ölçüm raporları ve kalite belgeleri hazırlıyoruz.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: 'Ölçüm Raporu Örneği',
                description: 'CMM ölçüm sonuçları ve geometrik tolerans kontrolü',
                icon: 'ri-file-chart-line',
                downloadUrl: '#'
              },
              {
                title: 'Malzeme Sertifikası',
                description: 'Ham madde kalite sertifikası ve kimyasal analiz',
                icon: 'ri-file-shield-line',
                downloadUrl: '#'
              },
              {
                title: 'Kaynak Prosedürü',
                description: 'WPS kaynak prosedürü ve kalifikasyon raporu',
                icon: 'ri-file-settings-line',
                downloadUrl: '#'
              }
            ].map((report, index) => (
              <Card key={index} hover className="text-center">
                <div className="w-16 h-16 bg-[#C1121F] rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className={`${report.icon} text-white text-2xl`}></i>
                </div>
                <h3 className="text-xl font-semibold text-[#161616] mb-4">
                  {report.title}
                </h3>
                <p className="text-gray-600 mb-6">
                  {report.description}
                </p>
                <Button variant="outline" size="sm" className="border-[#C1121F] text-[#C1121F] hover:bg-[#C1121F] hover:text-white">
                  <i className="ri-download-line mr-2"></i>
                  PDF İndir
                </Button>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#C1121F]">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-white mb-6" style={{ fontFamily: '"Poppins", sans-serif' }}>
            Kalite Garantili Üretim İçin İletişime Geçin
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            ISO standartlarında kalite güvencesi ile projelerinizi hayata geçirelim.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/teklif-al">
              <Button variant="secondary" size="lg">
                <i className="ri-file-upload-line mr-2"></i>
                Kaliteli Üretim İçin Teklif Al
              </Button>
            </Link>
            <Link to="/iletisim">
              <Button variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-[#C1121F]">
                <i className="ri-phone-line mr-2"></i>
                Kalite Ekibi ile Görüş
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
